import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'
import Router from 'next/router'

import MerchantSkuCard from './MerchantSkuCard'
import useSkus from 'hooks/useSkus'
import { useErrorStore } from 'store/errorStore'
import { PrevButton, NextButton } from './CustomButton'
import { tabletSize } from 'theme'
import { COUPON_SCREEN_NAME, EVENT_ACTION, BUTTON_NAME } from 'tracking'

const MerchantSkus = ({ merchantId, skuId, isFullPage, trackEvent }) => {
  const { wrapErrorHandler } = useErrorStore()
  const [swiper, updateSwiper] = useState(null)
  const [merchantSkus, setMerchantSkus] = useState([])
  const [progress, setProgress] = useState(0)

  const { dataSource, showLoadingPlaceholder } = useSkus({
    wrapErrorHandler,
    merchantId,
    limit: 13,
  })
  const clickSlide = useCallback(
    (data) => {
      trackEvent(EVENT_ACTION.CLICK, {
        screen: COUPON_SCREEN_NAME.SKU,
        button: BUTTON_NAME.COUPON,
        item_id: data.id,
        item_type: 'sku',
        item_status: 'available',
        related_item_id: skuId,
        related_item_type: 'sku',
      })
      Router.push(`/sku/${data.hid}`)
    },
    [skuId, trackEvent]
  )
  const swiperItem = useCallback(
    (data, index) => (
      <SwiperSlide key={data.id}>
        <MerchantSkuCard
          onclickSlide={() => clickSlide(data)}
          data={data}
          index={index}
          key={data.id}
        />
      </SwiperSlide>
    ),
    [clickSlide]
  )

  const updateProgress = useCallback(() => setProgress(swiper.progress), [
    swiper,
  ])

  const params = {
    initialSlide: 0,
    slidesPerView: 4,
    spaceBetween: 30,
    slidesPerGroup: 4,
    onSwiper: updateSwiper,
    breakpoints: {
      1024: {
        slidesPerView: 4,
        spaceBetween: 16,
      },
      600: {
        slidesPerView: 3,
        spaceBetween: 8,
      },
      320: {
        slidesPerView: 2.5,
        spaceBetween: 8,
      },
    },
  }

  useEffect(() => {
    if (swiper) {
      swiper.update()
      swiper.on('progress', updateProgress)
    }
    return () => {
      if (swiper) {
        swiper.destroy()
        swiper.off('progress', updateProgress)
      }
    }
  }, [swiper, updateProgress])

  useEffect(() => {
    if (dataSource && dataSource.length) {
      setMerchantSkus(
        dataSource.filter((item) => item.id !== skuId).splice(0, 12)
      )
    }
  }, [dataSource, skuId])

  const goNext = () => {
    if (swiper) {
      swiper.slideNext()
    }
  }

  const goPrev = () => {
    if (swiper) {
      swiper.slidePrev()
    }
  }

  if (showLoadingPlaceholder || merchantSkus.length === 0) {
    return null
  }

  return (
    <Wapper isFullPage={isFullPage}>
      <Title>同商戶其他優惠</Title>
      <SwiperWapper>
        <Swiper {...params}>{merchantSkus.map(swiperItem)}</Swiper>
        <PrevButton hide={progress <= 0.4} onClick={goPrev} />
        <NextButton
          hide={progress >= 1 || (swiper && swiper.isEnd)}
          onClick={goNext}
        />
      </SwiperWapper>
    </Wapper>
  )
}

const Wapper = styled.div`
  padding: ${(p) => (p.isFullPage ? '24px 0' : '24px')};
  background: ${(p) => p.theme.color.blue20};
  width: 100%;
  box-sizing: border-box;
  @media ${tabletSize} {
    padding: ${(p) => (p.isFullPage ? '24px 0' : '24px 16px')};
  }
`
const Title = styled.div`
  color: ${(p) => p.theme.color.navigationTitleColor};
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  &:before {
    content: ' ';
    width: 4px;
    height: 16px;
    background: ${(p) => p.theme.color.blue200};
    margin-right: 8px;
  }
`

const SwiperWapper = styled.div`
  position: relative;
`

export default MerchantSkus
